/**
 * The ONLY place createTheme() is called.
 * Assembles all foundation modules and component overrides.
 *
 * Imports directly from foundation files, never from index.ts,
 *
 *  FIGMA reference:
 *  https://www.figma.com/design/CqO24eewdxuBg3MkLbj2QX/BHB-components?node-id=6112-51415&t=cqV7qec26xmWuSV7-0
 */
import { grey } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

// Component overrides
import { dialogOverrides } from "./components/dialog";
import { toolbarOverrides } from "./components/toolbar";
// Breakpoints
import { buildBreakpoints } from "./foundations/breakpoints";
// Foundations
import { colorTokens, textTokens, uiTokens } from "./foundations/colors";
import { layoutTokens } from "./foundations/layout";
import { typographyVariants } from "./foundations/typography";

export const createAppTheme = () => {
  const theme = createTheme({
    palette: {
      primary: {
        main: colorTokens.primary,
        light: colorTokens.primary50,
        dark: "#0D2C45"
      },
      secondary: {
        main: colorTokens.secondary
      },
      error: {
        main: colorTokens.error
      },
      text: {
        // Kept as original rgba strings to not affect existing components.
        // New components should use textTokens.dark.* directly.
        primary: "rgba(0, 0, 0, .87)",
        secondary: "rgba(0, 0, 0, .54)",
        disabled: "rgba(0, 0, 0, .38)"
      },
      background: {
        default: grey[100]
      },
      header: {
        // v2 — new design tokens
        newMain: colorTokens.primary,
        newSub: textTokens.dark.primary,
        newByline: textTokens.dark.primary,
        // v1 — TODO: remove after new header design is approved and implemented
        main: colorTokens.lightest,
        sub: grey[50],
        byline: "#5c5c5c"
      },
      separator: grey[200],
      accent: {
        iconBackground: colorTokens.primary900,
        containerBackground: colorTokens.primary900,
        // v2 — TODO: rename and remove containerBackground once migrated
        containerBackgroundNew: colorTokens.brightBlue,
        text: textTokens.dark.primary,
        disabled: colorTokens.dark38,
        bookingSuccess: colorTokens.primary50
      },
      button: {
        borderColor: colorTokens.transparencyDarkAt23,
        textColor: textTokens.dark.secondary
      },
      // v2 — Figma "Tokens" collection (1-to-1 mapping)
      tokens: uiTokens,
      // v2 — Figma "Text" collection
      textTokens
    },
    breakpoints: buildBreakpoints(),
    typography: { ...typographyVariants },
    // v2 — layout tokens available via useTheme().layout
    layout: layoutTokens,
    components: {
      ...dialogOverrides,
      ...toolbarOverrides,
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            dialogHeader: "h5",
            bodyHeader: "p",
            caption: "p"
          }
        }
      }
    }
  });

  return theme;
};
